import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiCalendar, FiArrowRight } = FiIcons;

const Experience = () => {
  const experiences = [
    {
      title: 'Java Software Engineer',
      company: 'Capital One',
      location: 'United States',
      period: 'Jul 2024 - Present',
      description: 'Developing Spring Boot microservices for financial rewards systems and building responsive React.js interfaces for enhanced user experience.',
      technologies: ['Spring Boot', 'React.js', 'Kafka', 'Redis', 'Material UI', 'Redux', 'JWT', 'AWS', 'Kubernetes', 'JUnit5', 'Mockito'],
      achievements: [
        'Developed Spring Boot microservices for rewards redemption, JWT-secured authentication, and real-time transaction tracking using Kafka, improving checkout latency by 40%',
        'Built responsive React.js UI for rewards management using Material UI, Redux, and Formik, enhancing user experience and reducing support queries by 25%',
        'Designed Redis-based caching layers to cut API response time from 300ms to under 100ms for high-frequency GET requests',
        'Implemented CI/CD pipelines with GitHub Actions/Jenkins for Dockerized deployments to AWS EC2, RDS, and Kubernetes, accelerating release cycles by 30%',
        'Maintained 90%+ code coverage with JUnit5, Mockito, and H2 database testing, ensuring high reliability and early bug detection'
      ]
    },
    {
      title: 'Software Engineer',
      company: 'Epic Systems',
      location: 'United States',
      period: 'May 2021 - Jul 2023',
      description: 'Engineered healthcare software solutions with Spring MVC and Angular, focusing on patient data systems and clinical tools with HIPAA compliance.',
      technologies: ['Spring MVC', 'Angular', 'Hibernate ORM', 'MySQL', 'RxJS', 'Spring Security', 'JSP', 'SSO', 'RBAC'],
      achievements: [
        'Engineered Spring MVC modules for patient data and billing systems using Hibernate ORM and MySQL stored procedures, streamlining backend operations and reducing data latency',
        'Developed Angular components for appointment booking and lab dashboards with RxJS and reactive forms, improving booking success rate by 35%',
        'Integrated Single Sign-On (SSO) and Spring Security with Role-Based Access Control (RBAC), ensuring HIPAA-compliant access across clinical tools',
        'Built exportable PDF/CSV dashboards using JSP and backend reporting logic, saving clinical staff hours per week in manual tracking',
        'Contributed to legacy-to-modern data migration with backend validation scripts, reducing onboarding friction for new EHR modules'
      ]
    },
    {
      title: 'Full-stack Developer',
      company: 'Target',
      location: 'United States',
      period: 'Jun 2019 - May 2021',
      description: 'Built scalable microservices for retail operations and inventory management systems, enabling millions of daily product syncs across platforms.',
      technologies: ['Spring Boot', 'Angular', 'JWT', 'Spring Security', 'Jenkins', 'Git', 'Tomcat', 'JSP', 'Microservices'],
      achievements: [
        'Built modular Spring Boot microservices for catalog updates and vendor integration, enabling millions of daily product syncs across in-store and online platforms',
        'Designed Angular-based inventory UI with reusable components and lazy loading, reducing product management time for store teams by 40%',
        'Implemented JWT-secured authentication integrated with Angular route guards and Spring Security, strengthening platform security and session management',
        'Automated CI/CD workflows using Jenkins and Git, deploying to on-prem Tomcat servers and minimizing manual deployment errors',
        'Bridged legacy JSP modules with the new microservice architecture, ensuring zero-downtime migration during production rollout'
      ]
    }
  ];

  return (
    <section id="experience" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 mb-4">
            Professional Experience
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            My journey as a Full Stack Developer across different organizations
          </p>
        </motion.div>

        <div className="space-y-8">
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {exp.title}
                  </h3>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
                    <span className="text-primary-600 font-medium">{exp.company}</span>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                        {exp.location}
                      </div>
                      <div className="flex items-center gap-1">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        {exp.period}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-gray-600 mb-4">{exp.description}</p>

              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Achievements:</h4>
                <ul className="space-y-1">
                  {exp.achievements.map((achievement, achIndex) => (
                    <li key={achIndex} className="flex items-start gap-2 text-sm text-gray-600">
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4 mt-0.5 text-primary-600 flex-shrink-0" />
                      {achievement}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-wrap gap-2">
                {exp.technologies.map((tech, techIndex) => (
                  <span
                    key={techIndex}
                    className="px-3 py-1 bg-primary-100 text-primary-800 text-xs rounded-full"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;